import { tv } from 'tailwind-variants';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { RemixIcon } from 'helpers/RemixIcon';
import { getTestProps } from 'lib/testing/utils';
import useDictionary from 'lib/hooks/useDictionary';
import { BoardMemberItemProps } from '../../components/authorable/shared/content/BoardMembers';
import type { Field, ImageField, LinkField, RichTextField } from '@sitecore-content-sdk/nextjs';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';

type BoardMemberFields = {
  firstname?: Field<string>;
  lastname?: Field<string>;
  quoteText?: RichTextField;
  picture?: ImageField;
  jobtitle?: Field<string>;
  linkedInURL?: Field<string>;
};

const BoardMemberItem = (props: BoardMemberItemProps) => {
  const isEditing = useIsEditing();
  const RenderingIdentifier = props?.id;
  const { getDictionaryValue } = useDictionary();
  const { firstname, lastname, quoteText, picture, jobtitle, linkedInURL } =
    props.fields as BoardMemberFields;
  const boardMemberName = { value: `${firstname?.value} ${lastname?.value}` };
  const boardMemberRole = { value: jobtitle?.value };
  const boardMemberLinkedInLink = { value: { href: linkedInURL?.value, target: '_blank' } };
  const getBoardMemberProfileUrl = (currentUrl: string) => {
    // Remove single letter slug pattern: /our-people/[a-z]/[slug] -> /our-people/[slug]
    if (currentUrl.includes('/our-people/')) {
      // Match pattern: /our-people/[single-letter-a-z]/[rest-of-path]
      const pattern = /\/our-people\/([a-z])\/(.+)/;
      const match = currentUrl.match(pattern);
      if (match) {
        // Return /our-people/[rest-of-path]
        return `/our-people/${match[2]}`;
      }
    }
    return currentUrl;
  };
  const itemUrl = typeof props.url === 'string' ? props.url : undefined;
  const boardMemberCtaLink = itemUrl
    ? ({
        value: {
          href: getBoardMemberProfileUrl(itemUrl),
          text: getDictionaryValue('BoardMemberCardCtaText') || 'Find out more',
        },
      } as LinkField)
    : undefined;
  // const styles = parseStyleParams(props.params, ['cta1']);

  const {
    container,
    cardWrapper,
    imageSection,
    contentSection,
    contentTop,
    contentBottom,
    imageWrapper,
    testimonialQuote,
    boardMemberDetails,
    boardMemberNameTV,
    boardMemberRoleTV,
    linkedInWrapper,
    linkedInIcon,
    separator,
    ctaWrapper,
    ctaLink,
  } = TAILWIND_VARIANTS();
  return (
    <article
      className={container()}
      data-component="helpers/BoardMember/BoardMemberCard"
      {...editingHydrationProps(isEditing)}
      id={RenderingIdentifier}
      {...getTestProps(`board-member-card-${RenderingIdentifier}`)}
    >
      <div className={cardWrapper()}>
        {/* Image Section */}
        <div className={imageSection()}>
          {picture?.value?.src ? (
            <ImageWrapper
              field={picture}
              className={imageWrapper()}
              {...getTestProps(`board-member-image`)}
            />
          ) : (
              <ImageWrapper
                field={{
                  value: {
                    src: '/assets/profile_placeholder.png',
                    alt: 'No image',
                  },
                }}
                className={imageWrapper()}
                {...getTestProps(`related-profile-image-placeholder`)}
              />
          )}
        </div>

        {/* Content Section - Dark Blue Background */}
        <div className={contentSection()}>
          <div className={contentTop()}>
            {/* Testimonial Quote */}
            {quoteText?.value && (
              <RichTextWrapper
                field={quoteText}
                className={`text-component-boardMember-testimonial ${testimonialQuote()}`}
                {...getTestProps(`boardMember-testimonial`)}
              />
            )}

            {/* BoardMember Details */}
            <div className={boardMemberDetails()}>
              <div>
                <PlainTextWrapper
                  className={boardMemberNameTV()}
                  field={boardMemberName}
                  tag="div"
                  {...getTestProps(`board-member-name`)}
                />
                <PlainTextWrapper
                  className={boardMemberRoleTV()}
                  field={boardMemberRole}
                  tag="div"
                  {...getTestProps(`board-member-role`)}
                />
              </div>

              {/* LinkedIn Link */}
              {boardMemberLinkedInLink?.value?.href && (
                <LinkWrapper
                  ctaComponentClass="component-card-item-button-1-color"
                  field={boardMemberLinkedInLink}
                  className={linkedInWrapper()}
                  editable={false}
                  aria-label={`View ${boardMemberName?.value || 'Board Member'} LinkedIn profile`}
                  suppressNewTabIcon={true}
                  {...getTestProps(`board-member-linkedin`)}
                >
                  <RemixIcon name="linkedin-box-fill" size="xl" className={linkedInIcon()} />
                </LinkWrapper>
              )}
            </div>
          </div>

          {/* Separator and CTA - Float to bottom on tablet */}
          <div className={contentBottom()}>
            {/* Separator */}
            {boardMemberCtaLink?.value?.href && <div className={separator()}></div>}

            {/* CTA Link */}
            {boardMemberCtaLink?.value?.href && (
              <div className={ctaWrapper()}>
                <LinkWrapper
                  ctaComponentClass="component-card-item-button-1-color"
                  className={ctaLink()}
                  field={boardMemberCtaLink}
                  ctaIcon="arrow-right"
                  ctaIconSize="s"
                  ctaStyle={getCtaStyle({ ctaVariant: 'tertiary' }, 'tertiary')}
                  aria-label={
                    boardMemberCtaLink?.value?.text
                      ? `${boardMemberCtaLink?.value?.text} for ${boardMemberName?.value || 'Board Member'}`
                      : `Find out more about ${boardMemberName?.value || 'Board Member'}`
                  }
                  {...getTestProps(`board-member-cta`)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default BoardMemberItem;

export const TAILWIND_VARIANTS = tv({
  slots: {
    container: ['w-full basis-full lg:basis-[calc(33.333%-1rem)]'],
    cardWrapper: [
      'w-full',
      'h-full',
      'flex',
      'flex-col',
      'md:flex-row',
      'lg:flex-col',
      'overflow-hidden',
      'rounded-general-border-radius-card',
      'shadow-color-black-alpha-10',
      'shadow-[0_0_8px_0]',
    ],
    imageSection: [
      'relative',
      'w-full',
      'md:w-1/2',
      'lg:w-full',
      'overflow-hidden',
      'aspect-[4/3]',
      'md:aspect-square',
      'lg:aspect-[4/3]',
    ],
    contentSection: [
      'flex',
      'flex-col',
      'flex-1',
      'w-full',
      'md:w-1/2',
      'lg:w-full',
      'gap-spacing-spacing-24',
      'py-component-card-item-landscape-content-1-col-padding-y',
      'px-component-card-item-landscape-content-1-col-padding-x',
      'bg-component-featureCard-surface',
    ],
    imageWrapper: ['w-full', 'h-full', 'object-cover'],
    contentTop: ['flex', 'flex-col', 'gap-spacing-spacing-24'],
    testimonialQuote: [
      'text-white',
      'font-typography-font-family-header',
      'text-typography-font-size-header-small',
      'font-normal',
      'leading-typography-line-height-small',
      'text-left',
      'relative',
    ],
    boardMemberDetails: ['flex', 'items-start', 'justify-between', 'gap-spacing-spacing-16'],
    boardMemberNameTV: [
      'text-component-featureCard-subtitle',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-semibold',
      'text-left',
      'leading-typography-line-height-xsmall',
    ],
    boardMemberRoleTV: [
      'text-component-featureCard-subtitle',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-normal',
      'text-left',
      'leading-typography-line-height-xsmall',
    ],
    linkedInWrapper: [
      'flex',
      'w-10',
      'h-10',
      '!p-0',
      'text-white',
      'items-center',
      'justify-center',
      'hover:opacity-80',
      'transition-opacity',
      'duration-200',
      '[&:hover_svg]:!text-white',
    ],
    linkedInIcon: ['text-white'],
    separator: ['w-full', 'h-[2px]', 'bg-white'],
    ctaWrapper: ['flex', 'p-spacing-spacing-2', 'items-center', 'justify-between', 'self-stretch'],
    contentBottom: ['flex', 'flex-col', 'gap-spacing-spacing-24', 'mt-auto'],
    ctaLink: [
      '!text-white',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-semibold',
      'leading-typography-line-height-xxsmall',
      'flex',
      'items-center',
      'justify-between',
      'w-full',
      'hover:opacity-80',
      'transition-opacity',
      'duration-200',
      '[&:hover_svg]:!text-white',
    ],
  },
});
